/**
 * FocusCard — shared building block used by every role's FocusPanel.
 *
 * Renders one action-item block: a tone-tinted icon corner, a count, a
 * label, optional preview rows, and a "see all →" link. Empty blocks
 * collapse to a calm "nothing here" state.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Sparkles, Inbox } from 'lucide-react';

const TONES = {
  blue: 'border-blue-200 bg-blue-50/40',
  amber: 'border-amber-200 bg-amber-50/40',
  violet: 'border-violet-200 bg-violet-50/40',
  rose: 'border-rose-200 bg-rose-50/40',
  emerald: 'border-emerald-200 bg-emerald-50/40',
  slate: 'border-slate-200 bg-slate-50/40',
};

const TONE_COUNT = {
  blue: 'text-blue-700',
  amber: 'text-amber-700',
  violet: 'text-violet-700',
  rose: 'text-rose-700',
  emerald: 'text-emerald-700',
  slate: 'text-slate-700',
};

export function FocusCard({
  block,
  renderRow,
  emptyHint = "Nothing to action right now",
  testIdPrefix,
}) {
  const tone = TONES[block?.tone] || TONES.slate;
  const countTone = TONE_COUNT[block?.tone] || TONE_COUNT.slate;
  const testId = `${testIdPrefix}-${block?.key}`;

  if (block?.unavailable) {
    // The downstream entitlement isn't on for this org — keep the card
    // visible but muted, so the user knows the slot exists.
    return (
      <div className={`rounded-2xl border ${TONES.slate} p-5 opacity-60`}
            data-testid={testId} data-state="unavailable">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">
          {block.label}
        </div>
        <div className="text-sm text-slate-500">
          Not available on your current plan
        </div>
      </div>
    );
  }

  const rows = block?.rows || [];
  const count = block?.count ?? 0;
  const empty = count === 0 || rows.length === 0;

  return (
    <div className={`rounded-2xl border ${tone} p-5 flex flex-col gap-3`}
          data-testid={testId} data-state={empty ? 'empty' : 'has-items'}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            {block?.label}
          </div>
          <div className={`text-3xl font-semibold mt-1 ${countTone}`}
                data-testid={`${testId}-count`}>
            {count ?? 0}
          </div>
        </div>
        {block?.more_href && (
          <Link to={block.more_href}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid={`${testId}-see-all`}>
            See all <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      {empty ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Inbox className="w-4 h-4 text-slate-300" /> {emptyHint}
        </div>
      ) : (
        <ul className="divide-y divide-slate-200/60 -mx-1">
          {rows.map((row, idx) => (
            <li key={row?.id || idx}
                className="py-2 px-1 flex items-center justify-between gap-3 text-sm"
                data-testid={`${testId}-row-${idx}`}>
              {renderRow ? renderRow(row, idx) : (
                <span className="text-slate-700">{row?.patient_initials || row?.id}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function FocusHero({ title, subtitle, primaryAction, testId = 'dashboard-focus-hero' }) {
  return (
    <div className="flex items-start justify-between gap-6 flex-wrap"
          data-testid={testId}>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Today
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-2">{title}</h1>
        {subtitle && (
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>
        )}
      </div>
      {primaryAction && (
        // Iter 158.1 · Use react-router <Link> instead of a raw <a> so
        // clicking the CTA doesn't force a full-page reload. On the
        // planner shell in particular, the CTA points to
        // ``/planner/dashboard?refer=1`` — a query-param deep-link
        // that the parent page listens for to open the referral
        // intake modal. A raw <a href> reload caused a blank render
        // in the K8s preview and hid the modal from planners.
        <Link to={primaryAction.href}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            data-testid="dashboard-focus-primary-action">
          {primaryAction.label} <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}
